export const SALES_POINT_TAG_PALETTE_SIZE = 128;

export interface SalesPointTagColors {
  /** Light-mode pastel fill (dark text on soft hue). */
  readonly backgroundColor: string;
  /** Light-mode text. */
  readonly color: string;
  /**
   * Dark-mode fill — muted hue at low lightness (matches soft-token pattern:
   * translucent tint on charcoal, not light-mode pastels).
   */
  readonly darkBackgroundColor: string;
  /** Dark-mode text — bright hue for contrast on charcoal. */
  readonly darkColor: string;
}

const GOLDEN_ANGLE_DEGREES = 137.50776405003785;

function hslToHex(hue: number, saturation: number, lightness: number): string {
  const normalizedHue = ((hue % 360) + 360) % 360;
  const s = saturation / 100;
  const l = lightness / 100;
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const huePrime = normalizedHue / 60;
  const x = chroma * (1 - Math.abs((huePrime % 2) - 1));
  let r = 0;
  let g = 0;
  let b = 0;

  if (huePrime >= 0 && huePrime < 1) {
    r = chroma;
    g = x;
  } else if (huePrime < 2) {
    r = x;
    g = chroma;
  } else if (huePrime < 3) {
    g = chroma;
    b = x;
  } else if (huePrime < 4) {
    g = x;
    b = chroma;
  } else if (huePrime < 5) {
    r = x;
    b = chroma;
  } else {
    r = chroma;
    b = x;
  }

  const match = l - chroma / 2;
  const toHex = (channel: number): string => {
    const value = Math.round((channel + match) * 255);
    return value.toString(16).padStart(2, '0');
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function generateSalesPointTagPalette(size: number): readonly SalesPointTagColors[] {
  const palette: SalesPointTagColors[] = [];
  for (let index = 0; index < size; index += 1) {
    const hue = (index * GOLDEN_ANGLE_DEGREES) % 360;
    const saturationTier = index % 4;
    const lightnessTier = Math.floor(index / 4) % 4;
    const lightSaturation = 58 + saturationTier * 9;
    const backgroundLightness = 84 + lightnessTier * 2.5;
    const textLightness = 22 + (saturationTier % 2) * 4;
    /* Dark: soft tinted wells + bright text (same hue family, no pastel glare). */
    const darkSaturation = 40 + saturationTier * 6;
    const darkBackgroundLightness = 20 + lightnessTier * 2.5;
    const darkTextLightness = 78 + (saturationTier % 2) * 4;
    palette.push({
      backgroundColor: hslToHex(hue, lightSaturation, backgroundLightness),
      color: hslToHex(hue, Math.min(90, lightSaturation + 12), textLightness),
      darkBackgroundColor: hslToHex(hue, darkSaturation, darkBackgroundLightness),
      darkColor: hslToHex(hue, Math.min(72, darkSaturation + 18), darkTextLightness),
    });
  }
  return palette;
}

export const SALES_POINT_TAG_PALETTE = generateSalesPointTagPalette(SALES_POINT_TAG_PALETTE_SIZE);

const FALLBACK_TAG_COLORS: SalesPointTagColors = {
  backgroundColor: '#f8fafc',
  color: '#334155',
  darkBackgroundColor: '#27272a',
  darkColor: '#d4d4d8',
};

function circularPaletteDistance(a: number, b: number, size: number): number {
  const diff = Math.abs(a - b);
  return Math.min(diff, size - diff);
}

function pickPaletteIndex(used: ReadonlySet<number>, preferredStart: number): number {
  const normalizedPreferred =
    ((preferredStart % SALES_POINT_TAG_PALETTE_SIZE) + SALES_POINT_TAG_PALETTE_SIZE) %
    SALES_POINT_TAG_PALETTE_SIZE;

  if (used.size === 0) {
    return normalizedPreferred;
  }

  if (used.size >= SALES_POINT_TAG_PALETTE_SIZE) {
    return normalizedPreferred;
  }

  let bestIndex = 0;
  let bestScore = Number.NEGATIVE_INFINITY;
  for (let candidate = 0; candidate < SALES_POINT_TAG_PALETTE_SIZE; candidate += 1) {
    if (used.has(candidate)) {
      continue;
    }
    const minDistance = Math.min(
      ...[...used].map((usedIndex) =>
        circularPaletteDistance(candidate, usedIndex, SALES_POINT_TAG_PALETTE_SIZE),
      ),
    );
    const proximityPenalty =
      circularPaletteDistance(candidate, normalizedPreferred, SALES_POINT_TAG_PALETTE_SIZE) *
      0.01;
    const score = minDistance - proximityPenalty;
    if (score > bestScore) {
      bestScore = score;
      bestIndex = candidate;
    }
  }
  return bestIndex;
}

/** Assigns distinct palette indices for all tenant sales points (up to 128 unique colors). */
export function buildSalesPointColorIndexMap(
  tenantSalesPointIds: readonly number[],
): ReadonlyMap<number, number> {
  const uniqueSortedIds = [...new Set(tenantSalesPointIds)].sort((left, right) => left - right);
  const map = new Map<number, number>();
  const usedIndices = new Set<number>();
  const count = uniqueSortedIds.length;

  for (let position = 0; position < count; position += 1) {
    const salesPointId = uniqueSortedIds[position];
    if (salesPointId === undefined) {
      continue;
    }
    const preferredIndex =
      count <= SALES_POINT_TAG_PALETTE_SIZE
        ? Math.floor((position * SALES_POINT_TAG_PALETTE_SIZE) / count)
        : position % SALES_POINT_TAG_PALETTE_SIZE;
    const paletteIndex = pickPaletteIndex(usedIndices, preferredIndex);
    usedIndices.add(paletteIndex);
    map.set(salesPointId, paletteIndex);
  }

  return map;
}

export function getSalesPointTagStyle(paletteIndex: number): SalesPointTagColors {
  const normalizedIndex =
    ((paletteIndex % SALES_POINT_TAG_PALETTE_SIZE) + SALES_POINT_TAG_PALETTE_SIZE) %
    SALES_POINT_TAG_PALETTE_SIZE;
  return SALES_POINT_TAG_PALETTE[normalizedIndex] ?? FALLBACK_TAG_COLORS;
}

/** CSS custom properties consumed by `KIOSK_TAG_BASE_CLASS` (light + dark). */
export function getSalesPointTagCssVars(
  paletteIndex: number,
): Readonly<Record<'--sp-tag-bg' | '--sp-tag-fg' | '--sp-tag-bg-dark' | '--sp-tag-fg-dark', string>> {
  const colors = getSalesPointTagStyle(paletteIndex);
  return {
    '--sp-tag-bg': colors.backgroundColor,
    '--sp-tag-fg': colors.color,
    '--sp-tag-bg-dark': colors.darkBackgroundColor,
    '--sp-tag-fg-dark': colors.darkColor,
  };
}

/**
 * Theme-aware kiosk/sales-point pill.
 * Light: pastel fill + dark text. Dark: muted tinted well + bright text (no pastel glare).
 */
export const KIOSK_TAG_BASE_CLASS =
  'inline-flex max-w-full min-w-0 items-center gap-1 rounded-md px-2.5 py-1 text-xs font-medium bg-[var(--sp-tag-bg)] text-[var(--sp-tag-fg)] dark:bg-[var(--sp-tag-bg-dark)] dark:text-[var(--sp-tag-fg-dark)] ring-1 ring-inset ring-black/5 dark:ring-white/10';
